import { ChevronLeft, ChevronRight } from 'lucide-react'

interface Props {
    currentPage: number
    totalPages: number
    onPageChange: (page: number) => void
}

const Pagination = ({ currentPage, totalPages, onPageChange }: Props) => {
    const generatePageNumbers = () => {
        const pages: (number | string)[] = []
        const rangeAroundCurrent = 2

        pages.push(1)

        const startRange = Math.max(2, currentPage - rangeAroundCurrent)
        const endRange = Math.min(
            totalPages - 1,
            currentPage + rangeAroundCurrent
        )

        if (startRange > 2) {
            pages.push('...')
        }

        for (let i = startRange; i <= endRange; i++) {
            if (!pages.includes(i)) {
                pages.push(i)
            }
        }
        if (endRange < totalPages - 1) {
            pages.push('...')
        }

        if (totalPages > 1 && !pages.includes(totalPages)) {
            pages.push(totalPages)
        }

        return pages
    }

    const pages = generatePageNumbers()

    return (
        <nav
            aria-label="Page navigation"
            className="flex items-center justify-center gap-2"
        >
            <ul className="flex items-center gap-1">
                {/* Previous button */}
                <li>
                    <button
                        onClick={() => onPageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                        aria-label="Previous page"
                    >
                        <ChevronLeft size={18} />
                    </button>
                </li>

                {/* Page numbers */}
                {pages.map((page, index) => (
                    <li key={`${page}-${index}`}>
                        {page === '...' ? (
                            <span className="px-3 py-2 text-gray-700 dark:text-gray-300">
                                ...
                            </span>
                        ) : (
                            <button
                                onClick={() => onPageChange(page as number)}
                                className={`inline-flex items-center justify-center rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                                    currentPage === page
                                        ? 'border-green-600 bg-green-600 text-white'
                                        : 'bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                                }`}
                                aria-label={`Go to page ${page}`}
                                aria-current={
                                    currentPage === page ? 'page' : undefined
                                }
                            >
                                {page}
                            </button>
                        )}
                    </li>
                ))}

                {/* Next button */}
                <li>
                    <button
                        onClick={() => onPageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="inline-flex items-center justify-center rounded-md border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                        aria-label="Next page"
                    >
                        <ChevronRight size={18} />
                    </button>
                </li>
            </ul>
        </nav>
    )
}

export default Pagination
